import { AfterViewInit, Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { CmsComponent, Product, WindowRef } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { distinctUntilChanged } from 'rxjs';
import { TrustedShopsService } from '../../../services/trustedshops.service';
import { default as widgetSettings } from './widget-settings.json';
@Component({
  selector: 'app-custom-trustedshops-widget',
  templateUrl: './custom-trustedshops-widget.component.html',
  standalone: false
})
export class CustomTrustedShopsWidgetComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() widgetType: string;
  @Input() product: Product;

  protected iframeClass: string;
  protected widgetId: string;
  protected htmlTemplate: string[] = [];
  protected title: string;
  protected observer: ResizeObserver;

  @HostBinding('class') styleClasses: string;
  @ViewChild('iframeRef', { static: false }) iframeRef!: ElementRef<HTMLIFrameElement>;

  constructor(
    protected winRef: WindowRef,
    protected renderer: Renderer2,
    protected component: CmsComponentData<CmsComponent>,
    protected trustedShopsService: TrustedShopsService
  ) {}

  ngAfterViewInit(): void {
    if (!this.winRef.isBrowser()) {
      return;
    }
    this.iframeRef.nativeElement?.addEventListener('load', this.resizeIframe(this.iframeRef.nativeElement));
    if (this.widgetType) {
      if (widgetSettings[this.widgetType]) {
        this.styleClasses = this.widgetType;
        this.setWidgetData(widgetSettings[this.widgetType]);
        this.updateIframeContent();
      }
    } else {
      // If used as a CMS component, get the settings based on the component UID
      this.component.data$.pipe(distinctUntilChanged()).subscribe((component) => {
        const uid = component?.uid;
        if (widgetSettings[uid]) {
          this.styleClasses = uid;
          this.setWidgetData(widgetSettings[uid]);
          this.updateIframeContent();
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.winRef.isBrowser()) return;
    if (changes['product'] && !changes['product'].firstChange) {
      this.updateIframeContent();
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  resizeIframe(iframeEl: HTMLIFrameElement) {
    return () => {
      const doc = iframeEl.contentDocument || iframeEl.contentWindow.document;
      this.observer = new ResizeObserver((entries) => {
        window.requestAnimationFrame(() => {
          if (!entries[0] || !doc.body) return;

          const newHeight = doc.body.scrollHeight;
          this.renderer.setStyle(iframeEl, 'height', newHeight + 'px');
        });
      });
      this.observer.observe(doc.body);
    };
  }

  private setWidgetData(settings: any): void {
    if (settings) {
      this.iframeClass = settings.class;
      this.widgetId = settings.widgetId;
      this.htmlTemplate = settings.htmlTemplate;
      this.title = settings.title || '';
    }
  }

  private updateIframeContent(): void {
    if (!this.htmlTemplate || this.htmlTemplate.length === 0 || !this.widgetId) {
      return;
    }
    const htmlContent = this.generateTrustedShopsHtml(this.product?.code);
    const iframe = this.iframeRef?.nativeElement;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    if (doc) {
      this.observer?.disconnect();
      this.observer = null;
      doc.open();
      doc.write(htmlContent);
      doc.close();
    }
    this.renderer.addClass(this.iframeRef.nativeElement, this.iframeClass);
  }

  private generateTrustedShopsHtml(code?: string): string {
    const htmlString = this.htmlTemplate.join('').replace('$widgetId', this.widgetId);
    if (code) {
      return htmlString.replace('$productCode', code);
    }
    return htmlString;
  }
}
